import { useCallback, useMemo } from "react";

import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import useGetPortfolioFamilies from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioFamilies";
import useGetPortfolioFamiliesById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioFamiliesById";
import useGetPortfolioPlanById, {
  PortfolioPlanByIdResponse,
} from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanById";
import useGetPortfolioPlans from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlans";
import { PortfolioFamiliesResponse } from "@fartherfinance/frontend/api/Investment/request/getPortfolioFamilies";
import { PortfolioFamiliesByIdResponse } from "@fartherfinance/frontend/api/Investment/request/getPortfolioFamiliesById";
import {
  PersonId,
  PortfolioPlanResponse,
} from "@fartherfinance/frontend/api/Investment/types";
import {
  ClientId,
  HookResult,
  PortfolioFamilyId,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";

import { ModelPortfolioTypeOptions } from "../components/TransitionProposals/TransitionProposalFlowSteps/Share/constants";
import { ModelPortfolioQueryParams } from "../components/TransitionProposals/TransitionProposalFlowSteps/Share/types";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { State } from "@src/store";

interface Props {
  queryParams: ModelPortfolioQueryParams;
  page?: string;
  pageSize?: string;
  search?: string;
}

type QueryDataMap = {
  fartherList: HookResult<PortfolioFamiliesResponse>;
  fartherModelMarketPlace: HookResult<PortfolioFamiliesResponse>;
  fartherFamilyById: HookResult<PortfolioFamiliesByIdResponse>;
  portfolioPlanById: HookResult<PortfolioPlanByIdResponse>;
  sharedByAdvisor: HookResult<PortfolioPlanResponse>;
  customPortfolios: HookResult<PortfolioPlanResponse>;
};

export function useGetModelPortfolioByName({
  queryParams,
  page = "1",
  pageSize = "100",
  search = "",
}: Props): QueryDataMap {
  const { advisorId } = useSelector((state: State) => ({
    advisorId: state.main_Reducer.cockroach_advisor_id,
  }));

  const { clientId } = useParams<{ clientId: ClientId }>();

  const auth = useAdvisorRequestAuth();

  const getAuthByQueryName = useCallback(
    (qName: ModelPortfolioQueryParams["name"]) => {
      return queryParams?.name === qName ? auth : null;
    },
    [auth, queryParams]
  );

  const portfolioFamiliesByIdId = useMemo(() => {
    const decoded = PortfolioFamilyId.safeParse(queryParams?.id);
    return decoded.success ? decoded.data : null;
  }, [queryParams]);

  const portfolioPlanByIdId = useMemo(() => {
    const decoded = PortfolioPlanId.safeParse(queryParams?.id);
    return decoded.success ? decoded.data : undefined;
  }, [queryParams]);

  const portfolioFamiliesByIdResponse = useGetPortfolioFamiliesById(
    portfolioFamiliesByIdId,
    portfolioFamiliesByIdId !== null ? auth : null
  );

  const portfolioPlanByIdResponse = useGetPortfolioPlanById(
    portfolioPlanByIdId !== undefined ? portfolioPlanByIdId : null,
    {},
    portfolioPlanByIdId !== undefined ? auth : null
  );

  const fartherAssetManagementResponse = useGetPortfolioFamilies(
    { page, pageSize },
    getAuthByQueryName(ModelPortfolioTypeOptions.FARTHER_LIST)
  );

  const fartherMarketPlaceResponse = useGetPortfolioFamilies(
    { page, pageSize },
    getAuthByQueryName(ModelPortfolioTypeOptions.FARTHER_MODEL_MARKET_PLACE)
  );

  const advisorPortfolioResponse = useGetPortfolioPlans(
    {
      visibleTo: PersonId.parse(advisorId),
      notOwnedBy: PersonId.parse(clientId),
      basePermissions: ["Private"],
      pageSize,
      page,
      orderBy: "name asc",
      search,
    },
    getAuthByQueryName(ModelPortfolioTypeOptions.SHARED_BY_ADVISOR)
  );

  const clientPortfolioResponse = useGetPortfolioPlans(
    {
      ownedBy: PersonId.parse(clientId),
      basePermissions: ["Private"],
      pageSize,
      page,
      orderBy: "name asc",
      search,
    },
    getAuthByQueryName(ModelPortfolioTypeOptions.CUSTOM_PORTFOLIOS)
  );

  const queryResponses = useMemo(() => {
    return {
      fartherList: fartherAssetManagementResponse,
      fartherModelMarketPlace: fartherMarketPlaceResponse,
      fartherFamilyById: portfolioFamiliesByIdResponse,
      portfolioPlanById: portfolioPlanByIdResponse,
      sharedByAdvisor: advisorPortfolioResponse,
      customPortfolios: clientPortfolioResponse,
    };
  }, [
    fartherAssetManagementResponse,
    fartherMarketPlaceResponse,
    portfolioFamiliesByIdResponse,
    advisorPortfolioResponse,
    clientPortfolioResponse,
    portfolioPlanByIdResponse,
  ]);

  return queryResponses;
}
